import { useMemo, useCallback } from 'react';
import { userDataService } from '../services/UserDataService';
import { useAuthContext } from '../contexts/AuthContext';

export const useStreamAuth = () => {
  const {
    user,
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    logout: authLogout,
    deletion,
  } = useAuthContext();

  // Sign out, drop this viewer's state, then go to `logoutParams.returnTo` if given.
  const logout = useCallback(
    (options = {}) => {
      userDataService.clearCurrentUser();

      authLogout(options).finally(() => {
        if (options?.logoutParams?.returnTo) {
          window.location.assign(options.logoutParams.returnTo);
        }
      });
    },
    [authLogout],
  );

  const userRolesSet = useMemo(() => {
    if (!user) {
      return new Set();
    }
    const roles = user['https://bkfc.com/roles'] || [];
    return new Set(roles);
  }, [user]);

  // Single hasRole function to avoid duplication
  const hasRole = useMemo(() => {
    return role => userRolesSet.has(role);
  }, [userRolesSet]);

  const permissions = useMemo(() => {
    return {
      canStream: hasRole('streamer') || hasRole('admin'),
      canViewPremium: isAuthenticated,
      canModerate: hasRole('admin') || hasRole('moderator'),
      canManageUsers: hasRole('admin'),
      isStreamer: hasRole('streamer'),
      isAdmin: hasRole('admin'),
      isModerator: hasRole('moderator'),
      isPremiumUser: hasRole('premium') || hasRole('streamer') || hasRole('admin'),
    };
  }, [hasRole, isAuthenticated]);

  const getUserPreferences = useMemo(() => {
    if (!user) {
      return {};
    }

    return {
      theme: user['https://bkfc.com/theme'] || 'dark',
      language: user['https://bkfc.com/language'] || 'en',
      notifications: user['https://bkfc.com/notifications'] !== false,
      autoplay: user['https://bkfc.com/autoplay'] !== false,
      quality: user['https://bkfc.com/quality'] || 'auto',
    };
  }, [user]);

  const getStreamingSettings = useMemo(() => {
    if (!permissions.canStream) {
      return null;
    }

    return {
      maxBitrate: hasRole('premium') ? 8000 : 4000,
      maxResolution: hasRole('premium') ? '1080p' : '720p',
      canUseCustomThumbnails: hasRole('premium') || hasRole('admin'),
      canScheduleStreams: hasRole('premium') || hasRole('admin'),
      maxStreamDuration: hasRole('premium') ? 0 : 4 * 60 * 60, // 4 hours for free users, unlimited for premium
    };
  }, [permissions.canStream, hasRole]);

  return {
    user,
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    logout,
    deletion,
    permissions,
    userPreferences: getUserPreferences,
    streamingSettings: getStreamingSettings,
    hasRole,
  };
};
